import React from 'react'
import { Trans, useLingui } from '@lingui/react/macro'

import AdvancedSlider from './AdvancedSlider'
import { type AdvancedSettingsValue, DEFAULT_ADVANCED_SETTINGS } from './shared'

import { CssIcon } from '@/components/CssIcon'
import InfoPopover from '@/pages/tad.ai/_components/InfoPopover'


import { cls } from 'tagged-classnames-free';
interface AdvancedSettingsProps {
  value?: Partial<AdvancedSettingsValue>
  onChange?: (value: AdvancedSettingsValue) => void
  showAudioWeight?: boolean
  /** 受控模式：展开状态 */
  expanded?: boolean
  /** 受控模式：展开状态变更回调 */
  onExpandedChange?: (expanded: boolean) => void
}

function AdvancedSettings(props: AdvancedSettingsProps) {
  const {
    value,
    onChange,
    showAudioWeight = true,
    expanded: controlledExpanded,
    onExpandedChange,
  } = props
  const mergedValue: AdvancedSettingsValue = {
    ...DEFAULT_ADVANCED_SETTINGS,
    ...value,
  }
  const { t } = useLingui()
  const [internalExpanded, setInternalExpanded] = React.useState(false)

  // 支持受控/非受控两种模式
  const isControlled = controlledExpanded !== undefined
  const expanded = isControlled ? controlledExpanded : internalExpanded
  const setExpanded = (next: boolean) => {
    if (!isControlled) setInternalExpanded(next)
    onExpandedChange?.(next)
  }

  const handleChange = (
    key: keyof AdvancedSettingsValue,
    val: AdvancedSettingsValue[keyof AdvancedSettingsValue],
  ) => {
    onChange?.({
      ...mergedValue,
      [key]: val,
    })
  }

  const sliderItems = [
    {
      key: 'styleWeight' as const,
      label: t`Style Weight`,
      description: t`The higher the value, the more closely the output adheres to the selected style, and vice versa.`,
    },
    {
      key: 'creativeWeight' as const,
      label: t`Creative Weight`,
      description: t`The higher the value, the more creative the generated output, and vice versa.`,
    },
    {
      key: 'audioWeight' as const,
      label: t`Audio Weight`,
      description: t`The higher the value, the more the generated output references the uploaded audio, and vice versa.`,
    },
  ].filter((item) => showAudioWeight || item.key !== 'audioWeight')

  return (
    <section>
      <button
        type='button'
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        className={cls`flex w-full items-center justify-between gap-4`}
      >
        <span className={cls`flex items-baseline gap-1.5`}>
          <span className={cls`text-f-text text-lg font-semibold`}>
            <Trans>Advanced Settings</Trans>
          </span>
          <span className={cls`text-f-text-quaternary text-base font-semibold`}>
            <Trans>(Optional)</Trans>
          </span>
        </span>
        <CssIcon
          className={cls`
            text-f-text i-com--down size-[18px] transition-transform
            ${expanded ? 'rotate-180' : 'rotate-0'}
          `}
        />
      </button>

      {expanded && (
        <div className={cls`flex flex-col gap-4 pt-4`}>
          <div className={cls`flex flex-col gap-2`}>
            <div className={cls`text-f-text-secondary text-base`}>
              <Trans>Vocal</Trans>
            </div>

            <div className={cls`flex items-center gap-2`}>
              {[
                { label: t`Male`, value: 'm' as const },
                { label: t`Female`, value: 'f' as const },
              ].map((item) => {
                const selected = mergedValue.vocalGender === item.value

                return (
                  <button
                    key={item.value}
                    type='button'
                    onClick={() =>
                      handleChange(
                        'vocalGender',
                        selected ? undefined : item.value,
                      )
                    }
                    className={cls`
                      rounded px-6 py-1 text-base transition-all
                      ${
                        selected
                          ? 'bg-f-primary-style-1 text-f-primary'
                          : 'text-f-text-tertiary hover:text-f-primary bg-[#252533]'
                      }
                    `}
                  >
                    {item.label}
                  </button>
                )
              })}
            </div>
          </div>

          {sliderItems.map((item) => {
            const currentValue = mergedValue[item.key]

            return (
              <div key={item.key} className={cls`flex flex-col gap-2`}>
                <div className={cls`flex items-center gap-1`}>
                  <span className={cls`text-f-text-secondary text-base`}>
                    {item.label}
                  </span>
                  <InfoPopover content={item.description} />
                </div>

                <AdvancedSlider
                  value={currentValue}
                  onChange={(nextValue) => handleChange(item.key, nextValue)}
                />
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default AdvancedSettings
